import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery } from '@tanstack/react-query';
import { useMapStore } from '../stores';
import { api } from '../utils/api';
import POIPopup from '../components/POIPopup';
import { Search, Navigation, Info, Phone, Menu, X, Layers, MapPin, Calendar, AlertTriangle, ChevronRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import Logo from '../components/Logo';

// POI category icons and colors
const POI_STYLES = {
  metro: { color: '#00a651', icon: 'Ⓜ️' },
  rail: { color: '#ff9800', icon: '🚂' },
  suburban: { color: '#2196f3', icon: '🚆' },
  tram: { color: '#f44336', icon: '🚊' },
  ev_charger: { color: '#10b981', icon: '⚡' },
  parking: { color: '#3b82f6', icon: '🅿️' },
  bike_station: { color: '#8b5cf6', icon: '🚲' },
};

// Create custom icon function - supports emoji, FontAwesome, Flaticon, and URL
const createCustomIcon = (category, iconUrl = null) => {
  // If custom icon URL provided, use it
  if (iconUrl) {
    // FontAwesome icon (e.g., "fa-solid fa-bus")
    if (iconUrl.startsWith('fa-')) {
      return L.divIcon({
        className: 'custom-marker-fontawesome',
        html: `
          <div style="
            font-size: 32px;
            line-height: 1;
            cursor: pointer;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
            transition: transform 0.2s ease;
            color: #3b82f6;
          ">
            <i class="${iconUrl}"></i>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    }
    
    // Flaticon icon (e.g., "fi-rr-marker")
    if (iconUrl.startsWith('fi-')) {
      return L.divIcon({
        className: 'custom-marker-flaticon',
        html: `
          <div style="
            font-size: 32px;
            line-height: 1;
            cursor: pointer;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
            transition: transform 0.2s ease;
            color: #3b82f6;
          ">
            <i class="${iconUrl}"></i>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    }
    
    // URL icon (e.g., "https://example.com/icon.png")
    if (iconUrl.startsWith('http')) {
      return L.icon({
        iconUrl: iconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    }
    
    // Emoji or custom text
    return L.divIcon({
      className: 'custom-marker-emoji',
      html: `
        <div style="
          font-size: 32px;
          line-height: 1;
          cursor: pointer;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
          transition: transform 0.2s ease;
        ">
          ${iconUrl}
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
  }
  
  // Default: Use category-based emoji
  const style = POI_STYLES[category] || { color: '#999999', icon: '📍' };
  
  return L.divIcon({
    className: 'custom-marker-simple',
    html: `
      <div style="
        font-size: 32px;
        line-height: 1;
        cursor: pointer;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
        transition: transform 0.2s ease;
      ">
        ${style.icon}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Normalize text for accent-insensitive search (Greek + Latin)
const normalizeText = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    // Greek specific replacements
    .replace(/ά/g, 'α')
    .replace(/έ/g, 'ε')
    .replace(/ή/g, 'η')
    .replace(/ί/g, 'ι')
    .replace(/ό/g, 'ο')
    .replace(/ύ/g, 'υ')
    .replace(/ώ/g, 'ω')
    .replace(/ΐ/g, 'ι')
    .replace(/ΰ/g, 'υ')
    .replace(/ς/g, 'σ'); // Final sigma to regular sigma
};

// Component to handle map view updates
function MapViewController({ onContextMenu }) {
  const { viewState, setViewState, setSelectedPOI } = useMapStore();
  const map = useMap();
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (viewState && !isUpdatingRef.current) {
      map.setView([viewState.latitude, viewState.longitude], viewState.zoom, { animate: false });
    }
  }, [viewState.latitude, viewState.longitude, viewState.zoom, map]);

  useEffect(() => {
    const handleMoveEnd = () => {
      isUpdatingRef.current = true;
      const center = map.getCenter();
      const zoom = map.getZoom();
      
      // Use setTimeout to avoid updating during render
      setTimeout(() => {
        setViewState({
          latitude: center.lat,
          longitude: center.lng,
          zoom: zoom
        });
        isUpdatingRef.current = false;
      }, 0);
    };

    const handleMapClick = (e) => {
      // Check if the click was stopped by a marker
      if (e.originalEvent._leaflet_stop || e.originalEvent._stopped) {
        return;
      }
      
      // Only close popup if clicking directly on the map (not on markers or popups)
      const target = e.originalEvent.target;
      if (target && (
        target.classList.contains('leaflet-container') || 
        target.classList.contains('leaflet-tile') ||
        target.classList.contains('leaflet-tile-pane') ||
        target.tagName === 'IMG'
      )) {
        setSelectedPOI(null);
      }
    };

    const handleContextMenu = (e) => {
      e.originalEvent.preventDefault();
      const { lat, lng } = e.latlng;
      const { clientX, clientY } = e.originalEvent;
      
      if (onContextMenu) {
        onContextMenu({ lat, lng, x: clientX, y: clientY });
      }
    };

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);
    map.on('click', handleMapClick);
    map.on('contextmenu', handleContextMenu);
    
    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
      map.off('click', handleMapClick);
      map.off('contextmenu', handleContextMenu);
    };
  }, [map, setViewState, setSelectedPOI, onContextMenu]);

  return null;
}

// Component to handle POI marker with popup
function POIMarker({ poi, isSelected, onSelect }) {
  const markerRef = useRef(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      if (isSelected) {
        marker.openPopup();
      } else {
        marker.closePopup();
      }
    }
  }, [isSelected]);

  return (
    <Marker
      ref={markerRef}
      position={[poi.lat, poi.lng]}
      icon={createCustomIcon(poi.category, poi.icon_url)}
      eventHandlers={{
        click: (e) => {
          // Prevent map click event from closing popup
          const originalEvent = e.originalEvent;
          if (originalEvent) {
            originalEvent._leaflet_stop = true;
            originalEvent.stopPropagation();
          }
          // Set selected POI
          onSelect(poi);
        }
      }}
    >
      {isSelected && (
        <Popup 
          onClose={() => onSelect(null)}
          maxWidth={280}
          minWidth={260}
          autoPan={false}
          closeButton={true}
          autoClose={false}
          closeOnClick={false}
        >
          <POIPopup poi={poi} />
        </Popup>
      )}
    </Marker>
  );
}

export default function MapView() {
  const { viewState, selectedPOI, setSelectedPOI, visibleLayers, toggleLayer, setViewState } = useMapStore();
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchMarker, setSearchMarker] = useState(null);
  const [isAISearching, setIsAISearching] = useState(false);
  const [aiSearchResults, setAISearchResults] = useState(null);
  const [isAIResultsView, setIsAIResultsView] = useState(false);
  
  // Collapsible section state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    marathon: true,
    events: true,
    categories: true,
    routing: true
  });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextLocation, setContextLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [isodistanceKm, setIsodistanceKm] = useState(5);
  const [showIsodistanceDialog, setShowIsodistanceDialog] = useState(false);
  const [isodistancePolygon, setIsodistancePolygon] = useState(null);
  const [showPOIsSidebar, setShowPOIsSidebar] = useState(false);
  const [nearbyPOIs, setNearbyPOIs] = useState([]);
  const [useOrsApi, setUseOrsApi] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Routing state
  const [routeStart, setRouteStart] = useState('');
  const [routeEnd, setRouteEnd] = useState('');
  
  // Marathon 2025 event layers state
  const [marathonEvent, setMarathonEvent] = useState(null);
  const [showMarathonLayers, setShowMarathonLayers] = useState(true);
  const [visibleMarathonLayers, setVisibleMarathonLayers] = useState({});

  // Debounce search query for geocoding API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch POIs
  const { data: poisData } = useQuery({
    queryKey: ['pois', visibleLayers],
    queryFn: () => {
      const categories = Object.keys(visibleLayers).filter(k => visibleLayers[k]);
      return api.getPOIs({ limit: 500 });
    },
  });

  // Fetch active events
  const { data: eventsData } = useQuery({
    queryKey: ['events', 'active'],
    queryFn: () => api.getEvents({ status: 'active' }),
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache
  });

  // Fetch Marathon 2025 event with layers
  const { data: marathonData, isLoading: isMarathonLoading, error: marathonError } = useQuery({
    queryKey: ['event', 'marathon-2025'],
    queryFn: () => api.getEvent('marathon-2025'),
  });

  // Update marathonEvent when data is fetched
  useEffect(() => {
    if (marathonData?.success && marathonData?.event) {
      setMarathonEvent(marathonData.event);
      console.log('Marathon event loaded:', marathonData.event);
      
      // Initialize all layers as visible
      if (marathonData.event.layers) {
        const initialVisibility = {};
        marathonData.event.layers.forEach(layer => {
          initialVisibility[layer.id] = true;
        });
        setVisibleMarathonLayers(initialVisibility);
      }
    }
  }, [marathonData]);

  useEffect(() => {
    if (marathonError) {
      console.log('Marathon event not found:', marathonError);
    }
  }, [marathonError]);

  // Search for locations using geocoding API (debounced)
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['geocode', debouncedSearchQuery],
    queryFn: async () => {
      if (debouncedSearchQuery.length < 3) return { results: [] };
      return api.geocodeSearch(debouncedSearchQuery);
    },
    enabled: debouncedSearchQuery.length >= 3,
    staleTime: 30000, // Cache for 30 seconds
  });

  const pois = poisData?.pois || [];
  const filteredPOIs = pois.filter(poi => visibleLayers[poi.category]);
  const events = eventsData?.events || [];

  // Define category info (used in search results)
  const categoryInfo = useMemo(() => ({
    metro: { icon: '🚇', label: 'Μετρό', color: 'bg-red-100 text-red-700 hover:bg-red-200', count: filteredPOIs.filter(p => p.category === 'metro').length },
    tram: { icon: '🚊', label: 'Τραμ', color: 'bg-green-100 text-green-700 hover:bg-green-200', count: filteredPOIs.filter(p => p.category === 'tram').length },
    rail: { icon: '🚆', label: 'Προαστιακός', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', count: filteredPOIs.filter(p => p.category === 'rail').length },
    suburban: { icon: '🚉', label: 'Σταθμοί ΟΣΕ', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200', count: filteredPOIs.filter(p => p.category === 'suburban').length },
    ev_charger: { icon: '⚡', label: 'Φορτιστές EV', color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200', count: filteredPOIs.filter(p => p.category === 'ev_charger').length },
    parking: { icon: '🅿️', label: 'Πάρκινγκ', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', count: filteredPOIs.filter(p => p.category === 'parking').length },
    bike_station: { icon: '🚲', label: 'Ποδήλατα', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200', count: filteredPOIs.filter(p => p.category === 'bike_station').length }
  }), [filteredPOIs]);

  // Combine local search (POIs/Events) with geocoding results
  const combinedSearchResults = useMemo(() => {
    if (searchQuery.length < 3) return [];
    
    const normalizedQuery = normalizeText(searchQuery);
    
    // Search in POIs
    const poiResults = pois
      .filter(poi => 
        normalizeText(poi.name).includes(normalizedQuery) ||
        normalizeText(poi.description).includes(normalizedQuery) ||
        normalizeText(poi.category).includes(normalizedQuery)
      )
      .map(poi => ({
        id: `poi-${poi.id}`,
        type: 'poi',
        name: poi.name,
        description: `${categoryInfo[poi.category]?.icon || '📍'} ${categoryInfo[poi.category]?.label || poi.category}`,
        lat: poi.lat,
        lng: poi.lng,
        data: poi
      }));
    
    // Search in Events
    const eventResults = events
      .filter(event => 
        normalizeText(event.title).includes(normalizedQuery) ||
        normalizeText(event.description).includes(normalizedQuery)
      )
      .map(event => ({
        id: `event-${event.id}`,
        type: 'event',
        name: event.title,
        description: `📅 Εκδήλωση`,
        lat: event.lat,
        lng: event.lng,
        data: event
      }));
    
    // Combine with geocoding results
    const geoResults = (searchResults?.results || []).map((result, idx) => ({
      id: `geo-${idx}`,
      type: 'geocoding',
      name: result.name,
      description: result.type || 'Τοποθεσία',
      lat: result.lat,
      lng: result.lng,
      data: result
    }));
    
    // Prioritize: POIs first, then Events, then geocoding
    return [...poiResults, ...eventResults, ...geoResults].slice(0, 8);
  }, [searchQuery, pois, events, searchResults, categoryInfo]);

  // Handle search result selection
  const handleSearchSelect = (result) => {
    if (result.type === 'poi') {
      // If it's a POI, open its popup directly
      setSelectedPOI(result.data);
      setSearchMarker(null);
    } else {
      // For geocoding results and events, show search marker
      setSearchMarker({
        lat: result.lat,
        lng: result.lng,
        name: result.name
      });
      setSelectedPOI(null);
    }
    
    // Move map to location
    setViewState({
      latitude: result.lat,
      longitude: result.lng,
      zoom: 17
    });
    
    setSearchQuery(result.name);
    setSearchFocused(false);
  };

  // Handle AI-powered search
  const handleAISearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      alert('Παρακαλώ εισάγετε τουλάχιστον 3 χαρακτήρες για αναζήτηση με AI');
      return;
    }

    setIsAISearching(true);
    setAISearchResults(null);

    try {
      const result = await api.aiSearchPOIs(searchQuery);
      setAISearchResults(result);
      
      // Show results in sidebar
      if (result.pois && result.pois.length > 0) {
        setNearbyPOIs(result.pois);
        setIsAIResultsView(true); // Flag to indicate AI results
        setShowPOIsSidebar(true);
        
        // Optionally zoom to first result
        const firstPOI = result.pois[0];
        setViewState({
          latitude: firstPOI.lat,
          longitude: firstPOI.lng,
          zoom: 13
        });
      } else {
        alert(`AI Αποτέλεσμα: ${result.aiExplanation}\n\nΔεν βρέθηκαν αποτελέσματα.`);
      }
    } catch (error) {
      console.error('AI search error:', error);
      alert('Σφάλμα στην AI αναζήτηση. Δοκιμάστε ξανά.');
    } finally {
      setIsAISearching(false);
    }
  };

  // Handle context menu
  const handleContextMenu = useCallback(async ({ lat, lng, x, y }) => {
    setContextMenu({ x, y });
    setContextLocation({ lat, lng });
    
    // Reverse geocode to get location name
    try {
      const result = await api.reverseGeocode(lat, lng);
      setLocationName(result.name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } catch (error) {
      setLocationName(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    }
  }, []);

  // Handle "What's Here" action
  const handleWhatsHere = () => {
    setSearchMarker({
      lat: contextLocation.lat,
      lng: contextLocation.lng,
      name: locationName
    });
    setViewState({
      latitude: contextLocation.lat,
      longitude: contextLocation.lng,
      zoom: 16
    });
    setContextMenu(null);
  };

  // Handle "Find POIs within X km" action
  const handleFindPOIs = () => {
    setShowIsodistanceDialog(true);
    setContextMenu(null);
  };

  // Calculate isodistance and find nearby POIs
  const calculateIsodistance = useCallback(async () => {
    if (!contextLocation) return;
    
    setIsCalculating(true);
    
    try {
      let polygonPoints = [];
      
      if (useOrsApi) {
        // Use ORS API for real driving distance isochrones
        const response = await fetch('https://ors-api.routing.telenavis.com/ors/v2/isochrones/driving-car', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8'
          },
          body: JSON.stringify({
            locations: [[contextLocation.lng, contextLocation.lat]],
            range: [isodistanceKm * 1000], // Convert km to meters
            range_type: 'distance'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Extract coordinates from GeoJSON response
          if (data.features && data.features[0] && data.features[0].geometry) {
            const coords = data.features[0].geometry.coordinates[0];
            polygonPoints = coords.map(coord => [coord[1], coord[0]]); // Swap to [lat, lng]
          }
        } else {
          throw new Error('ORS API failed');
        }
      } else {
        // Simple circular approximation (1 degree ≈ 111 km)
        const kmToDegrees = isodistanceKm / 111;
        
        for (let i = 0; i <= 360; i += 10) {
          const angle = (i * Math.PI) / 180;
          const lat = contextLocation.lat + (kmToDegrees * Math.cos(angle));
          const lng = contextLocation.lng + (kmToDegrees * Math.sin(angle) / Math.cos(contextLocation.lat * Math.PI / 180));
          polygonPoints.push([lat, lng]);
        }
      }
      
      setIsodistancePolygon(polygonPoints);
      
      // Find POIs within polygon using accurate PostGIS ST_Within query
      if (polygonPoints.length >= 3) {
        const response = await api.getPOIsWithinPolygon(polygonPoints);
        if (response.success) {
          setNearbyPOIs(response.pois);
          setIsAIResultsView(false); // Clear AI results flag
        } else {
          setNearbyPOIs([]);
        }
      } else {
        setNearbyPOIs([]);
      }
      
      setShowPOIsSidebar(true);
      setShowIsodistanceDialog(false);
    } catch (error) {
      console.error('Isodistance calculation failed:', error);
      alert('Σφάλμα στον υπολογισμό της απόστασης. Δοκιμάστε χωρίς ORS API.');
    } finally {
      setIsCalculating(false);
    }
  }, [contextLocation, isodistanceKm, pois, useOrsApi]);

  // Calculate distance in km using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Close context menu on any click
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const recentSearchExamples = [
    { icon: <MapPin size={16} />, text: 'Σταθμός Μετρό Σύνταγμα' },
    { icon: <MapPin size={16} />, text: 'Φορτιστής EV κοντά μου' },
    { icon: <MapPin size={16} />, text: 'Σταθμός Τραμ Νέο Φάληρο' }
  ];

  return (
    <div className="h-screen w-full flex flex-col bg-[#ecf0f1]">
      {/* Top Navigation Bar - Flat Design */}
      <header className="bg-white border-b-2 border-[#e0e6e8] z-[1001] relative">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            <Logo size="small" />
          </div>

          <div className="flex-1 max-w-2xl mx-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#7f8c8d]" size={20} />
              <input
                type="text"
                placeholder="Αναζήτηση σταθμού, φορτιστή EV, τοποθεσίας..."
                className="w-full pl-12 pr-12 py-3 rounded-md border-2 border-[#bdc3c7] focus:border-[#ED1C24] focus:outline-none text-[#2c3e50] bg-white placeholder-[#95a5a6] transition-colors"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              />
              {/* AI Button - Temporarily Disabled for Fine-tuning */}
              {false && (
                <button
                  onClick={handleAISearch}
                  disabled={isAISearching || searchQuery.length < 3}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-[#9b59b6] text-white px-3 py-1.5 rounded-md hover:bg-[#8e44ad] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 text-sm font-medium"
                  title="AI αναζήτηση με DeepSeek (Shift+Enter)"
                >
                  {isAISearching ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>AI...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      <span>AI</span>
                    </>
                  )}
                </button>
              )}
              {isSearching && searchQuery.length >= 3 && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-[#ED1C24] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {searchFocused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-md border-2 border-[#bdc3c7] p-4 z-50">
                  {searchQuery.length >= 3 && combinedSearchResults.length > 0 ? (
                    <>
                      <div className="text-sm font-semibold text-[#34495e] mb-3">Αποτελέσματα</div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {combinedSearchResults.map((result) => (
                          <button
                            key={result.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSearchSelect(result);
                            }}
                            className="w-full flex items-start p-3 hover:bg-[#ecf0f1] rounded-md cursor-pointer group text-left transition-colors"
                          >
                            <div className="text-[#7f8c8d] mr-3 mt-0.5">
                              {result.type === 'poi' ? (
                                <span className="text-base">{categoryInfo[result.data.category]?.icon || '📍'}</span>
                              ) : result.type === 'event' ? (
                                <Calendar size={16} />
                              ) : (
                                <MapPin size={16} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[#2c3e50] text-sm font-medium truncate">{result.name}</div>
                              <div className="text-[#7f8c8d] text-xs truncate">{result.description}</div>
                            </div>
                            <ChevronRight size={16} className="text-[#bdc3c7] group-hover:text-[#7f8c8d] mt-0.5 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </>
                  ) : searchQuery.length >= 3 && !isSearching ? (
                    <div className="text-center py-4 text-[#7f8c8d] text-sm">
                      Δεν βρέθηκαν αποτελέσματα
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-[#34495e] mb-3">Παραδείγματα αναζήτησης</div>
                      <div className="space-y-2">
                        {recentSearchExamples.map((search, idx) => (
                          <button
                            key={idx}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSearchQuery(search.text);
                            }}
                            className="w-full flex items-center p-3 hover:bg-[#ecf0f1] rounded-md cursor-pointer group text-left transition-colors"
                          >
                            <div className="text-[#7f8c8d] mr-3">{search.icon}</div>
                            <span className="text-[#34495e] text-sm flex-1">{search.text}</span>
                            <ChevronRight size={16} className="text-[#bdc3c7] group-hover:text-[#7f8c8d]" />
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <a
              href="https://telenavis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-3 py-2 bg-[#ED1C24] text-white rounded-md hover:bg-[#C91820] transition-colors"
            >
              <Info size={18} />
              <img 
                src="/TELENAVIS_NewLogo_REDback_72dpi.jpg" 
                alt="Telenavis" 
                className="h-6"
              />
            </a>
          </div>
        </div>
      </header>

      <div className="flex-1 flex relative overflow-hidden">
        {/* Sidebar */}
        {showSidebar && (
          <aside className="w-96 bg-white border-r-2 border-[#e0e6e8] z-[1000] overflow-y-auto">
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#2c3e50]">Χάρτης</h2>
                <button onClick={() => setShowSidebar(false)} className="hover:bg-[#ecf0f1] p-2 rounded-md transition-colors">
                  <X size={20} className="text-[#7f8c8d]" />
                </button>
              </div>

              {/* Marathon 2025 Section - Only show if event exists */}
              {marathonEvent && (() => {
                // Convert Tailwind color names to hex values
                const tailwindColors = {
                  'purple-600': '#9333ea',
                  'purple-700': '#7e22ce',
                  'pink-600': '#db2777',
                  'pink-700': '#be185d',
                  'blue-600': '#2563eb',
                  'blue-700': '#1d4ed8',
                  'green-600': '#16a34a',
                  'green-700': '#15803d',
                  'red-600': '#dc2626',
                  'red-700': '#b91c1c',
                  'indigo-600': '#4f46e5',
                  'indigo-700': '#4338ca',
                  'orange-600': '#ea580c',
                  'orange-700': '#c2410c',
                };

                const colorFrom = tailwindColors[marathonEvent.metadata?.ui?.colors?.from] || '#9333ea';
                const colorTo = tailwindColors[marathonEvent.metadata?.ui?.colors?.to] || '#db2777';
                const colorFromHover = tailwindColors[marathonEvent.metadata?.ui?.colors?.fromHover] || '#7e22ce';
                const colorToHover = tailwindColors[marathonEvent.metadata?.ui?.colors?.toHover] || '#be185d';

                return (
                  <div className="border-2 border-[#9b59b6] rounded-md overflow-hidden">
                    <button
                      onClick={() => setSectionsExpanded(prev => ({ ...prev, marathon: !prev.marathon }))}
                      className="w-full text-white p-4 flex items-center justify-between transition-colors bg-[#9b59b6] hover:bg-[#8e44ad]"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{marathonEvent.metadata?.ui?.icon || '📅'}</span>
                        <h3 className="text-lg font-bold">{marathonEvent.name.toUpperCase()}</h3>
                        {marathonEvent.metadata?.ui?.badge && (
                          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                            {marathonEvent.metadata.ui.badge}
                          </span>
                        )}
                      </div>
                      {sectionsExpanded.marathon ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  
                  {sectionsExpanded.marathon && (
                    <div className="p-4 bg-white space-y-3">
                      <div className="bg-white border-2 border-[#e0e6e8] rounded-md p-3">
                        <h4 className="font-bold text-[#2c3e50] mb-2">
                          {marathonEvent.metadata?.ui?.subtitle || 'Πληροφορίες'}
                        </h4>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={showMarathonLayers}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setShowMarathonLayers(checked);
                                // Toggle all layers
                                if (marathonEvent?.layers) {
                                  const allVisibility = {};
                                  marathonEvent.layers.forEach(layer => {
                                    allVisibility[layer.id] = checked;
                                  });
                                  setVisibleMarathonLayers(allVisibility);
                                }
                              }}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="text-sm font-semibold text-[#34495e]">Όλες οι διαδρομές</span>
                          </label>
                        </div>
                        
                        {marathonEvent?.layers && marathonEvent.layers.length > 0 && (
                          <div className="mt-3 pt-3 border-t-2 border-[#e0e6e8] space-y-2">
                            <div className="text-xs font-semibold text-[#7f8c8d] mb-2">Επιλογή Διαδρομών:</div>
                            {marathonEvent.layers
                              .filter(layer => layer.is_visible)
                              .sort((a, b) => a.display_order - b.display_order)
                              .map((layer) => (
                                <label key={layer.id} className="flex items-center space-x-2 cursor-pointer hover:bg-[#ecf0f1] p-1 rounded-md">
                                  <input
                                    type="checkbox"
                                    checked={visibleMarathonLayers[layer.id] !== false}
                                    onChange={(e) => {
                                      setVisibleMarathonLayers(prev => ({
                                        ...prev,
                                        [layer.id]: e.target.checked
                                      }));
                                      // Update master toggle if all are unchecked
                                      const anyChecked = marathonEvent.layers.some(l => 
                                        l.id === layer.id ? e.target.checked : visibleMarathonLayers[l.id] !== false
                                      );
                                      setShowMarathonLayers(anyChecked);
                                    }}
                                    className="w-3.5 h-3.5 text-purple-600 rounded"
                                  />
                                  <div 
                                    className="relative w-10 h-3 flex items-center"
                                    style={{ opacity: visibleMarathonLayers[layer.id] !== false ? 1 : 0.3 }}
                                  >
                                    {/* Render line with proper style (solid or dashed) */}
                                    {layer.style.dashArray ? (
                                      // Dashed line
                                      <svg width="40" height="12" className="absolute">
                                        <line
                                          x1="0"
                                          y1="6"
                                          x2="40"
                                          y2="6"
                                          stroke={layer.style.color}
                                          strokeWidth={layer.style.weight || 3}
                                          strokeDasharray={layer.style.dashArray}
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    ) : (
                                      // Solid line
                                      <div
                                        className="w-full rounded-full"
                                        style={{
                                          height: `${Math.min(layer.style.weight || 3, 4)}px`,
                                          backgroundColor: layer.style.color
                                        }}
                                      ></div>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-700 flex-1">{layer.name}</span>
                                  {layer.icon_url && layer.icon_url.startsWith('fa-') && (
                                    <i className={layer.icon_url} style={{ fontSize: '12px', color: layer.style.color }}></i>
                                  )}
                                </label>
                              ))}
                          </div>
                        )}
                        
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-xs text-gray-600">
                            📅 <strong>Ημερομηνία:</strong> {new Date(marathonEvent.start_date).toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                            ⏰ <strong>Ώρα έναρξης:</strong> {new Date(marathonEvent.start_date).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}<br/>
                            📍 <strong>Αφετηρία:</strong> {marathonEvent.metadata?.start_location || 'Μαραθώνας'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                );
              })()}

              {/* Events Section - Collapsible */}
              {events.length > 0 && (
                <div className="border-2 border-[#d35400] rounded-md overflow-hidden">
                  <button
                    onClick={() => setSectionsExpanded(prev => ({ ...prev, events: !prev.events }))}
                    className="w-full bg-[#e67e22] text-white p-4 flex items-center justify-between hover:bg-[#d35400] transition-all"
                  >
                    <div className="flex items-center space-x-2">
                      <Calendar size={20} />
                      <h3 className="text-lg font-bold">ΕΝΕΡΓΑ EVENTS</h3>
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{events.length}</span>
                    </div>
                    {sectionsExpanded.events ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {sectionsExpanded.events && (
                    <div className="p-4 bg-white space-y-3">
                      {events.map((event) => (
                        <div key={event.id} className="bg-white border-2 border-[#e0e6e8] rounded-md p-3">
                          <h4 className="font-bold text-[#2c3e50] mb-1">{event.name}</h4>
                          <div className="flex items-center space-x-3 text-sm text-[#7f8c8d]">
                            <span>📅 {new Date(event.start_date).toLocaleDateString('el-GR')}</span>
                            <span className="px-2 py-0.5 bg-[#27ae60] text-white rounded text-xs font-medium">Ενεργό</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* POI Categories Section - Collapsible */}
              <div className="border-2 border-[#3498db] rounded-md overflow-hidden">
                <button
                  onClick={() => setSectionsExpanded(prev => ({ ...prev, categories: !prev.categories }))}
                  className="w-full bg-[#3498db] text-white p-4 flex items-center justify-between hover:bg-[#2980b9] transition-all"
                >
                  <div className="flex items-center space-x-2">
                    <Layers size={20} />
                    <h3 className="text-lg font-bold">ΚΑΤΗΓΟΡΙΕΣ POI</h3>
                  </div>
                  {sectionsExpanded.categories ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                
                {sectionsExpanded.categories && (
                  <div className="p-4 bg-white space-y-2">
                    {/* 2 tiles per line grid */}
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(categoryInfo).map(([key, info]) => (
                        <button
                          key={key}
                          onClick={() => toggleLayer(key)}
                          className={`${info.color} rounded-md p-3 cursor-pointer hover:border-2 hover:border-[#ED1C24] transition-all text-left relative ${
                            visibleLayers[key] ? 'border-2 border-[#ED1C24]' : 'opacity-70 border-2 border-transparent'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center">
                            <span className="text-3xl mb-1">{info.icon}</span>
                            <div className="text-xs font-semibold mb-0.5">{info.label}</div>
                            <div className="text-xs opacity-75">{info.count} σημεία</div>
                          </div>
                          {visibleLayers[key] && (
                            <div className="absolute top-1 right-1 w-3 h-3 bg-[#ED1C24] rounded-full border-2 border-white"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Routing Section - Collapsible */}
              <div className="border-2 border-[#27ae60] rounded-md overflow-hidden">
                <button
                  onClick={() => setSectionsExpanded(prev => ({ ...prev, routing: !prev.routing }))}
                  className="w-full bg-[#27ae60] text-white p-4 flex items-center justify-between hover:bg-[#229954] transition-all"
                >
                  <div className="flex items-center space-x-2">
                    <Navigation size={20} />
                    <h3 className="text-lg font-bold">ΔΡΟΜΟΛΟΓΗΣΗ</h3>
                  </div>
                  {sectionsExpanded.routing ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                
                {sectionsExpanded.routing && (
                  <div className="p-4 bg-white space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#34495e] mb-1">Από:</label>
                      <input
                        type="text"
                        value={routeStart}
                        onChange={(e) => setRouteStart(e.target.value)}
                        placeholder="Εισάγετε αφετηρία..."
                        className="w-full px-3 py-2 border-2 border-[#bdc3c7] rounded-md text-sm focus:ring-2 focus:ring-[#27ae60] focus:border-[#27ae60]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#34495e] mb-1">Προς:</label>
                      <input
                        type="text"
                        value={routeEnd}
                        onChange={(e) => setRouteEnd(e.target.value)}
                        placeholder="Εισάγετε προορισμό..."
                        className="w-full px-3 py-2 border-2 border-[#bdc3c7] rounded-md text-sm focus:ring-2 focus:ring-[#27ae60] focus:border-[#27ae60]"
                      />
                    </div>
                    <button 
                      className="w-full bg-[#27ae60] text-white py-2 rounded-md font-semibold hover:bg-[#229954] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!routeStart || !routeEnd}
                    >
                      Υπολογισμός Διαδρομής
                    </button>
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="bg-[#ecf0f1] border-2 border-[#bdc3c7] rounded-md p-4">
                <div className="flex items-center mb-2">
                  <AlertTriangle className="text-[#3498db] mr-2" size={18} />
                  <h3 className="text-sm font-semibold text-[#2c3e50]">Πληροφορίες</h3>
                </div>
                <p className="text-xs text-[#34495e]">
                  Επιλέξτε κατηγορίες για να εμφανίσετε σημεία στο χάρτη. 
                  Κάντε δεξί κλικ για επιλογές αναζήτησης.
                </p>
              </div>


            </div>
          </aside>
        )}

        {/* Map Container */}
        <main className="flex-1 relative bg-[#ecf0f1]">
          {!showSidebar && (
            <button
              onClick={() => setShowSidebar(true)}
              className="absolute top-4 left-4 z-[1000] bg-white border-2 border-[#e0e6e8] p-3 rounded-md hover:bg-[#ecf0f1] transition-colors"
            >
              <Menu size={24} className="text-[#34495e]" />
            </button>
          )}

          <MapContainer
            center={[viewState.latitude, viewState.longitude]}
            zoom={viewState.zoom}
            style={{ width: '100%', height: '100%' }}
            zoomControl={false}
          >
            <MapViewController onContextMenu={handleContextMenu} />
            
            {/* TeleNavis Tiles */}
            <TileLayer
              url="https://map-proxy.telenavis.com/wmts/telenavis/GLOBAL_MERCATOR/{z}/{x}/{y}.png"
              attribution='&copy; TeleNavis | &copy; OpenStreetMap contributors'
              maxZoom={22}
            />

            {/* Marathon 2025 Routes */}
            {console.log('Marathon Debug:', {
              showMarathonLayers,
              hasMarathonEvent: !!marathonEvent,
              layersCount: marathonEvent?.layers?.length,
              layers: marathonEvent?.layers,
              visibleMarathonLayers
            })}
            {showMarathonLayers && marathonEvent?.layers?.map((layer) => {
              console.log('Rendering layer:', layer.name, layer.geometry?.coordinates?.length);
              return layer.layer_type === 'route' && layer.is_visible && visibleMarathonLayers[layer.id] !== false && (
                <Polyline
                  key={layer.id}
                  positions={layer.geometry.coordinates.map(coord => [coord[1], coord[0]])}
                  pathOptions={{
                    color: layer.style.color || '#3388ff',
                    weight: layer.style.weight || 3,
                    opacity: layer.style.opacity || 0.8,
                    dashArray: layer.style.dashArray || null,
                    lineCap: layer.style.lineCap || 'round',
                    lineJoin: layer.style.lineJoin || 'round'
                  }}
                >
                  <Popup>
                    <div className="p-2">
                      <div className="font-bold text-sm mb-1">{marathonEvent?.name || 'Event'}</div>
                      <div className="text-xs text-gray-600">{layer.name}</div>
                      {layer.metadata?.vehicle_type && (
                        <div className="text-xs text-gray-500 mt-1">
                          Τύπος: {layer.metadata.vehicle_type}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Polyline>
              );
            })}

            {/* Marathon 2025 Point Markers (for point-type layers with custom icons) */}
            {showMarathonLayers && marathonEvent?.layers?.map((layer) => {
              if (layer.layer_type === 'point' && layer.is_visible && visibleMarathonLayers[layer.id] !== false && layer.geometry?.type === 'Point') {
                const [lng, lat] = layer.geometry.coordinates;
                return (
                  <Marker
                    key={layer.id}
                    position={[lat, lng]}
                    icon={createCustomIcon(null, layer.icon_url)}
                  >
                    <Popup>
                      <div className="p-2">
                        <div className="font-bold text-sm mb-1">{marathonEvent?.name || 'Event'}</div>
                        <div className="text-xs text-gray-600">{layer.name}</div>
                        {layer.metadata && Object.keys(layer.metadata).length > 0 && (
                          <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                            {Object.entries(layer.metadata).map(([key, value]) => (
                              <div key={key}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              }
              return null;
            })}

            {/* Marathon 2025 Polygon Layers (zones, areas) */}
            {showMarathonLayers && marathonEvent?.layers?.map((layer) => {
              if (layer.layer_type === 'polygon' && layer.is_visible && visibleMarathonLayers[layer.id] !== false && layer.geometry?.type === 'Polygon') {
                const positions = layer.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                return (
                  <Polygon
                    key={layer.id}
                    positions={positions}
                    pathOptions={{
                      color: layer.style.color || '#3388ff',
                      weight: layer.style.weight || 3,
                      opacity: layer.style.opacity || 0.8,
                      fillColor: layer.style.fillColor || layer.style.color || '#3388ff',
                      fillOpacity: layer.style.fillOpacity || 0.2,
                      dashArray: layer.style.dashArray || null,
                      lineCap: layer.style.lineCap || 'round',
                      lineJoin: layer.style.lineJoin || 'round'
                    }}
                  >
                    <Popup>
                      <div className="p-2">
                        <div className="font-bold text-sm mb-1">{marathonEvent?.name || 'Event'}</div>
                        <div className="text-xs text-gray-600">{layer.name}</div>
                        {layer.metadata && Object.keys(layer.metadata).length > 0 && (
                          <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                            {Object.entries(layer.metadata).map(([key, value]) => (
                              <div key={key}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Polygon>
                );
              }
              return null;
            })}

            {/* POI Markers */}
            {filteredPOIs.map((poi) => (
              <POIMarker
                key={poi.id}
                poi={poi}
                isSelected={selectedPOI && selectedPOI.id === poi.id}
                onSelect={setSelectedPOI}
              />
            ))}

            {/* Marathon Event POIs with Custom Icons */}
            {showMarathonLayers && marathonEvent?.pois?.map((poi) => (
              <POIMarker
                key={`marathon-${poi.id}`}
                poi={poi}
                isSelected={selectedPOI && selectedPOI.id === poi.id}
                onSelect={setSelectedPOI}
              />
            ))}

            {/* Search Result Marker */}
            {searchMarker && (
              <Marker
                position={[searchMarker.lat, searchMarker.lng]}
                icon={L.divIcon({
                  className: 'search-result-marker',
                  html: `
                    <div style="
                      font-size: 40px;
                      line-height: 1;
                      cursor: pointer;
                      filter: drop-shadow(0 3px 6px rgba(239, 68, 68, 0.5));
                      animation: bounce 1s ease-in-out 3;
                    ">
                      📍
                    </div>
                  `,
                  iconSize: [40, 40],
                  iconAnchor: [20, 40],
                  popupAnchor: [0, -40]
                })}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stopPropagation(e);
                  }
                }}
              >
                <Popup 
                  maxWidth={280}
                  minWidth={200}
                  autoPan={false}
                  closeButton={true}
                  autoClose={true}
                >
                  <div style={{ padding: '8px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                      {searchMarker.name.split(',')[0]}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {searchMarker.name}
                    </div>
                    <button
                      onClick={() => setSearchMarker(null)}
                      style={{
                        marginTop: '8px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Καθαρισμός
                    </button>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Isodistance Polygon */}
            {isodistancePolygon && (
              <Polygon
                positions={isodistancePolygon}
                pathOptions={{
                  color: '#3b82f6',
                  fillColor: '#3b82f6',
                  fillOpacity: 0.2,
                  weight: 2
                }}
              />
            )}
          </MapContainer>

          {/* Map Controls */}
          <div className="absolute right-4 top-4 z-[1000] space-y-2">
            <div className="bg-white border-2 border-[#e0e6e8] rounded-md">
              <button className="p-3 hover:bg-[#ecf0f1] transition-colors block text-lg font-semibold text-[#34495e] w-full border-b-2 border-[#e0e6e8]">+</button>
              <button className="p-3 hover:bg-[#ecf0f1] transition-colors block text-lg font-semibold text-[#34495e] w-full">−</button>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t-2 border-[#e0e6e8] px-6 py-3 z-[1000]">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center space-x-6 text-xs text-[#7f8c8d]">
                <div>© 2025 Telenavis</div>
                <div>|</div>
                <div>Δεδομένα © Telenavis</div>
                {events.length > 0 && (
                  <>
                    <div>|</div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-1 animate-pulse"></div>
                      <span>Event: {events[0].name}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-4 text-xs">
                <button className="text-[#ED1C24] hover:text-[#C91820] font-medium">Όροι Χρήσης</button>
                <button className="text-[#ED1C24] hover:text-[#C91820] font-medium">Βοήθεια</button>
              </div>
            </div>
          </div>
        </main>

        {/* Context Menu */}
        {contextMenu && (
          <div
            className="fixed bg-white rounded-md border-2 border-[#bdc3c7] py-2 z-[2000] min-w-[240px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleWhatsHere}
              className="w-full px-4 py-3 text-left text-sm text-[#34495e] hover:bg-[#ecf0f1] flex items-center space-x-3 transition-colors"
            >
              <MapPin size={18} className="text-[#ED1C24] flex-shrink-0" />
              <span className="font-medium">Τι υπάρχει εδώ;</span>
            </button>
            <div className="border-t-2 border-[#e0e6e8] my-1"></div>
            <button
              onClick={handleFindPOIs}
              className="w-full px-4 py-3 text-left text-sm text-[#34495e] hover:bg-[#ecf0f1] flex items-center space-x-3 transition-colors"
            >
              <Layers size={18} className="text-[#ED1C24] flex-shrink-0" />
              <span className="font-medium">Εύρεση POIs εντός...</span>
            </button>
            <div className="px-4 py-2 text-xs text-[#7f8c8d] border-t-2 border-[#e0e6e8] mt-1 bg-[#ecf0f1]">
              📍 {locationName}
            </div>
          </div>
        )}

        {/* Isodistance Dialog */}
        {showIsodistanceDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2001]">
            <div className="bg-white rounded-md border-2 border-[#bdc3c7] p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-[#2c3e50] mb-4">Εύρεση POIs εντός απόστασης</h3>
              <p className="text-sm text-[#34495e] mb-4">
                Επιλέξτε απόσταση οδήγησης σε χιλιόμετρα από το σημείο:
              </p>
              
              {/* Distance Slider */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#34495e] mb-2">
                  Απόσταση (km)
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={isodistanceKm}
                  onChange={(e) => setIsodistanceKm(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-[#7f8c8d] mt-1">
                  <span>1 km</span>
                  <span className="font-semibold text-[#ED1C24]">{isodistanceKm} km</span>
                  <span>50 km</span>
                </div>
              </div>

              {/* ORS API Checkbox */}
              <div className="mb-6 p-3 bg-[#ecf0f1] border-2 border-[#bdc3c7] rounded-md">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useOrsApi}
                    onChange={(e) => setUseOrsApi(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-[#ED1C24] focus:ring-[#ED1C24] border-[#bdc3c7] rounded"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#2c3e50]">
                      Χρήση ORS API (πραγματική απόσταση οδήγησης)
                    </div>
                    <div className="text-xs text-[#7f8c8d] mt-1">
                      Υπολογίζει την πραγματική απόσταση με βάση το οδικό δίκτυο. 
                      Απενεργοποιήστε για απλό κυκλικό υπολογισμό.
                    </div>
                  </div>
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowIsodistanceDialog(false)}
                  className="flex-1 px-4 py-2 border-2 border-[#bdc3c7] rounded-md text-sm font-medium text-[#34495e] hover:bg-[#ecf0f1]"
                  disabled={isCalculating}
                >
                  Ακύρωση
                </button>
                <button
                  onClick={calculateIsodistance}
                  disabled={isCalculating}
                  className="flex-1 px-4 py-2 bg-[#ED1C24] text-white rounded-md text-sm font-medium hover:bg-[#C91820] disabled:bg-[#7f8c8d] disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isCalculating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Υπολογισμός...
                    </>
                  ) : (
                    'Εύρεση POIs'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* POIs Sidebar */}
        {showPOIsSidebar && (
          <div className="fixed right-0 top-0 bottom-0 w-80 bg-white border-l-2 border-[#e0e6e8] z-[1002] flex flex-col">
            <div className="bg-[#ED1C24] text-white p-4 border-b-2 border-[#C91820] flex items-center justify-between">
              <h3 className="font-semibold">
                {isAIResultsView ? (
                  <div className="flex items-center space-x-2">
                    <Sparkles size={18} />
                    <span>AI Αποτελέσματα ({nearbyPOIs.length})</span>
                  </div>
                ) : (
                  `POIs εντός ${isodistanceKm} km`
                )}
              </h3>
              <button
                onClick={() => {
                  setShowPOIsSidebar(false);
                  setIsodistancePolygon(null);
                  setIsAIResultsView(false);
                  setAISearchResults(null);
                }}
                className="p-1 hover:bg-white/20 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* AI Explanation if available */}
            {isAIResultsView && aiSearchResults?.aiExplanation && (
              <div className="bg-purple-50 border-b border-purple-100 p-3">
                <div className="flex items-start space-x-2">
                  <Sparkles size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-purple-800">{aiSearchResults.aiExplanation}</p>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto p-4">
              {nearbyPOIs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {isAIResultsView 
                    ? 'Δεν βρέθηκαν αποτελέσματα με την AI αναζήτηση'
                    : 'Δεν βρέθηκαν POIs εντός της επιλεγμένης απόστασης'
                  }
                </div>
              ) : (
                <div className="space-y-2">
                  {nearbyPOIs.map((poi) => {
                    // Calculate distance only if contextLocation exists (for isodistance results)
                    const distance = !isAIResultsView && contextLocation 
                      ? calculateDistance(
                          contextLocation.lat,
                          contextLocation.lng,
                          poi.lat,
                          poi.lng
                        )
                      : null;
                    
                    return (
                      <button
                        key={poi.id}
                        onClick={() => {
                          setSelectedPOI(poi);
                          setViewState({
                            latitude: poi.lat,
                            longitude: poi.lng,
                            zoom: 16
                          });
                        }}
                        className="w-full p-3 bg-[#ecf0f1] hover:bg-[#bdc3c7] rounded-md text-left transition-colors"
                      >
                        <div className="flex items-start space-x-2">
                          <span className="text-xl flex-shrink-0">
                            {categoryInfo[poi.category]?.icon || '📍'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-[#2c3e50] truncate">
                              {poi.name}
                            </div>
                            <div className="text-xs text-[#7f8c8d] mt-1">
                              {categoryInfo[poi.category]?.label || poi.category}
                            </div>
                            {distance !== null && (
                              <div className="text-xs text-[#ED1C24] mt-1">
                                {distance.toFixed(2)} km
                              </div>
                            )}
                            {poi.description && (
                              <div className="text-xs text-[#7f8c8d] mt-1 line-clamp-1">
                                {poi.description}
                              </div>
                            )}
                          </div>
                          <ChevronRight size={16} className="text-[#7f8c8d] flex-shrink-0 mt-1" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
